import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatCheckName, getQualityColor } from '../../utils/scanReportUtils';

/**
 * Resolves a MUI color name to an actual CSS color value for use in sx props.
 */
const COLOR_VALUES = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  default: 'grey.500',
};

/**
 * QualityChecksSummary renders a compact summary of quality checks with an
 * expandable list showing individual check results.
 *
 * Accepts any object with a `checks` map (key → boolean) and a `summary` object
 * with `passed_checks`, `total_checks`, and optionally `pass_rate`.
 *
 * @param {Object} props
 * @param {Object} props.qualityReport - Object containing `checks` and `summary`
 * @param {Object} props.qualityReport.checks - Map of check_name → boolean
 * @param {Object} props.qualityReport.summary - { passed_checks, total_checks, pass_rate }
 * @param {string} [props.title] - Optional section title
 * @param {boolean} [props.defaultExpanded] - Whether to start expanded
 * @param {boolean} [props.showProgressBar] - Whether to show the progress bar
 */
const QualityChecksSummary = ({
  qualityReport,
  title,
  defaultExpanded = false,
  showProgressBar = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const { checks = {}, summary = {} } = qualityReport || {};
  const { passed_checks = 0, total_checks = 0 } = summary;
  const passRate = total_checks > 0 ? (passed_checks / total_checks) * 100 : 0;
  const qualityColor = getQualityColor(summary);
  const allPassed = passed_checks === total_checks && total_checks > 0;

  const { passedChecks, failedChecks } = useMemo(() => {
    // Underscore-prefixed keys carry structured DETAIL for a check (e.g.
    // _pages_without_transactions, _transactions_outside_period), not a pass/fail
    // boolean. They are excluded from the pass rate on the backend too; without
    // this filter a detail list renders here as a bogus failed row.
    const entries = Object.entries(checks).filter(([key]) => !key.startsWith('_'));
    return {
      passedChecks: entries.filter(([, value]) => value === true),
      failedChecks: entries.filter(([, value]) => value !== true),
    };
  }, [checks]);

  if (!qualityReport || total_checks === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No quality checks available.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Summary row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          px: 1,
          py: 0.5,
          mx: -1,
        }}
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        aria-expanded={expanded}
        aria-label={`${title || 'Quality checks'}: ${passed_checks} of ${total_checks} passed. Click to ${expanded ? 'collapse' : 'expand'} details.`}
      >
        {title && (
          <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
            {title}
          </Typography>
        )}

        <Typography
          variant="body2"
          fontWeight={600}
          color={`${qualityColor}.main`}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {passed_checks}/{total_checks} passed
        </Typography>

        {showProgressBar && (
          <Box sx={{ flexGrow: 1, mx: 1, minWidth: 80 }}>
            <LinearProgress
              variant="determinate"
              value={passRate}
              color={qualityColor}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        {allPassed && (
          <Tooltip title="All checks passed">
            <CheckCircleIcon color="success" fontSize="small" />
          </Tooltip>
        )}

        <IconButton size="small" aria-hidden tabIndex={-1}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Expandable check list */}
      <Collapse in={expanded}>
        <List dense disablePadding sx={{ mt: 0.5 }}>
          {/* Show failed checks first */}
          {failedChecks.map(([key]) => (
            <ListItem key={key} disableGutters sx={{ py: 0.25, pl: 1 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CancelIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={formatCheckName(key)}
                primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
              />
            </ListItem>
          ))}
          {/* Then passed checks */}
          {passedChecks.map(([key]) => (
            <ListItem key={key} disableGutters sx={{ py: 0.25, pl: 1 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={formatCheckName(key)}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

QualityChecksSummary.propTypes = {
  qualityReport: PropTypes.shape({
    checks: PropTypes.objectOf(PropTypes.bool),
    summary: PropTypes.shape({
      passed_checks: PropTypes.number,
      total_checks: PropTypes.number,
      pass_rate: PropTypes.number,
    }),
  }),
  title: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  showProgressBar: PropTypes.bool,
};

export default QualityChecksSummary;